import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ShieldCheck,
  Hash,
  Send,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import { api, ApiError } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { PageSkeleton } from "@/components/common/LoadingSkeleton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { toast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import {
  QuestionCard,
  isAnswered,
  type QuestionType,
  type QuestionOptions,
} from "@/components/assessments/QuestionCard";

// ─────────────────────────────────────────────
// ExternalQuestionnaire
//
// Screen — /external/:token
// Public third-party sharing portal (no login, no sidebar — rendered under
// <ExternalLayout/>). State machine:
//   start -> request a PIN
//   pin   -> verify the 6-digit PIN, get a short-lived x-share-session token
//   fill  -> answer the scoped questionnaire, submit once
//   done  -> thank-you screen
// ─────────────────────────────────────────────

type Step = "start" | "pin" | "fill" | "done";

function sessionStorageKey(token: string) {
  return `share_session_${token}`;
}

// 6-digit PIN input rendered as individual cells (mirrors VendorLogin's PinInput).
function PinInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const digits = value.split("").slice(0, 6);
  while (digits.length < 6) digits.push("");

  function handleChange(idx: number, char: string) {
    const next = [...digits];
    next[idx] = char.replace(/\D/g, "").slice(-1);
    const joined = next.join("");
    onChange(joined);
    if (char && idx < 5) {
      document.getElementById(`ext-pin-${idx + 1}`)?.focus();
    }
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      document.getElementById(`ext-pin-${idx - 1}`)?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, 5);
    document.getElementById(`ext-pin-${focusIdx}`)?.focus();
  }

  return (
    <div className="flex gap-2">
      {digits.map((d, idx) => (
        <input
          key={idx}
          id={`ext-pin-${idx}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={idx === 0 ? handlePaste : undefined}
          className={cn(
            "h-12 w-12 rounded-control border bg-bg-input text-center",
            "text-[20px] font-mono font-semibold text-text-primary",
            "transition-colors focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent",
            d ? "border-accent" : "border-border-default"
          )}
        />
      ))}
    </div>
  );
}

// ── Questionnaire data shapes (GET /external/questionnaires/:token) ─────────
interface ExternalQuestion {
  id: string;
  code: string;
  text: string;
  type: QuestionType;
  required: boolean;
  sortOrder: number;
  options: QuestionOptions | null;
  helpText?: string | null;
}

interface ExternalDomain {
  id: string;
  code: string;
  name: string;
  questions: ExternalQuestion[];
}

interface ExternalQuestionnaireResponse {
  data: {
    shareId: string;
    expiresAt: string;
    assessment: {
      id: string;
      entity: { id: string; name: string } | null;
      schema: { id: string; name: string };
      domains: ExternalDomain[];
    };
  };
}

type AnswerMap = Map<string, { value: unknown; comment: string }>;

/** A WEIGHTED_MULTIPLE_CHOICE question with no options can never be answered
 *  (mirrors AssessmentFill's L-025 guard) — never let it block submit. */
function optionsEmpty(options: QuestionOptions | null): boolean {
  if (!options) return true;
  if (Array.isArray(options)) return options.length === 0;
  const choices = (options as { choices?: unknown[] }).choices;
  return Array.isArray(choices) ? choices.length === 0 : false;
}

export function ExternalQuestionnaire() {
  const { token = "" } = useParams<{ token: string }>();
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>(() =>
    localStorage.getItem(sessionStorageKey(token)) ? "fill" : "start"
  );
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>(new Map());
  const [activeDomainIdx, setActiveDomainIdx] = useState(0);
  const [submitOpen, setSubmitOpen] = useState(false);

  // Read fresh every render (never held in React state) — mirrors
  // VendorAssessmentFill's pattern so a just-written token is visible on the
  // very next render without extra plumbing.
  const sessionToken = localStorage.getItem(sessionStorageKey(token)) ?? "";

  function clearSession() {
    localStorage.removeItem(sessionStorageKey(token));
  }

  // (a) Request PIN --------------------------------------------------------
  const requestPinMutation = useMutation({
    mutationFn: () => api.post<{ message: string }>("/external/request-pin", { token }),
    onSuccess: () => {
      toast.success(
        t("external.checkEmailTitle"),
        t("external.checkEmailDesc")
      );
      setStep("pin");
    },
    onError: (err: Error) => {
      // The link is spent once responses were submitted — show the thank-you
      // state instead of a failure.
      if (err instanceof ApiError && err.code === "SHARE_ALREADY_SUBMITTED") {
        toast.success(t("external.alreadySubmittedTitle"), t("external.alreadySubmittedDesc"));
        setStep("done");
        return;
      }
      toast.error(t("external.pinSendFailed"), err.message);
    },
  });

  // (b) Verify PIN ----------------------------------------------------------
  const verifyPinMutation = useMutation({
    mutationFn: () =>
      api.post<{
        data: { verified: boolean; shareId: string; sessionToken: string; expiresAt: string };
      }>("/external/verify-pin", { token, pin }),
    onSuccess: (res) => {
      localStorage.setItem(sessionStorageKey(token), res.data.sessionToken);
      setPin("");
      setPinError(null);
      setStep("fill");
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.code === "SHARE_ALREADY_SUBMITTED") {
        toast.success(t("external.alreadySubmittedTitle"), t("external.alreadySubmittedDesc"));
        setStep("done");
        return;
      }
      const message = err instanceof Error ? err.message : t("external.invalidPin");
      const status = err instanceof ApiError ? err.status : undefined;
      setPinError(message);
      setPin("");
      if (status === 429) {
        toast.error(t("external.tooManyAttempts"), message);
      } else {
        toast.error(t("external.verificationFailed"), message);
      }
    },
  });

  // (c) Load the scoped questionnaire ---------------------------------------
  const {
    data: qData,
    isLoading: qLoading,
    error: qError,
  } = useQuery<ExternalQuestionnaireResponse>({
    queryKey: ["external-questionnaire", token],
    queryFn: () => api.get(`/external/questionnaires/${token}`, { "x-share-session": sessionToken }),
    enabled: step === "fill" && Boolean(token) && Boolean(sessionToken),
    retry: false,
  });

  // A 401 here means the verified session died (expired/revoked) — recover by
  // clearing it and sending the visitor back to request a fresh PIN, rather
  // than stranding them on a dead screen.
  useEffect(() => {
    if (step !== "fill" || !qError) return;
    const status = (qError as { status?: number }).status;
    if (status === 401) {
      localStorage.removeItem(sessionStorageKey(token));
      toast.error(
        t("external.sessionExpiredTitle"),
        t("external.sessionExpiredDesc")
      );
      setStep("start");
    }
  }, [qError, step, token]);

  // (submit) -----------------------------------------------------------------
  const submitMutation = useMutation({
    mutationFn: (responses: { questionId: string; response: unknown }[]) =>
      api.post<{ data: { count: number } }>(
        `/external/questionnaires/${token}/responses`,
        { responses },
        { "x-share-session": sessionToken }
      ),
    onSuccess: () => {
      // Submitting invalidates the session server-side (single submission) —
      // clear it locally too and go straight to the thank-you screen. Do NOT
      // re-fetch the questionnaire; the session is already dead.
      clearSession();
      setStep("done");
    },
    onError: (err: unknown) => {
      const status = err instanceof ApiError ? err.status : undefined;
      const message = err instanceof Error ? err.message : t("assessment.failedToSubmit");
      if (err instanceof ApiError && err.code === "SHARE_ALREADY_SUBMITTED") {
        clearSession();
        toast.success(t("external.alreadySubmittedTitle"), t("external.alreadySubmittedDesc"));
        setStep("done");
      } else if (status === 401) {
        clearSession();
        toast.error(
          t("external.sessionExpiredTitle"),
          t("external.submitSessionExpiredDesc")
        );
        setStep("start");
      } else {
        toast.error(t("external.submitFailed"), message);
      }
    },
  });

  function handleValueChange(questionId: string, value: unknown) {
    setAnswers((prev) => {
      const next = new Map(prev);
      next.set(questionId, { value, comment: prev.get(questionId)?.comment ?? "" });
      return next;
    });
  }

  function handleCommentChange(questionId: string, comment: string) {
    setAnswers((prev) => {
      const next = new Map(prev);
      next.set(questionId, { value: prev.get(questionId)?.value ?? "", comment });
      return next;
    });
  }

  // ── Derived (plain consts — safe below hooks, no further hooks follow) ───
  const assessment = qData?.data.assessment;
  const domains = assessment?.domains ?? [];
  const allQuestions = domains.flatMap((d) => d.questions);
  const answeredCount = allQuestions.filter((q) => isAnswered(q.type, answers.get(q.id)?.value)).length;
  const missingRequired = allQuestions.filter((q) => {
    if (!q.required) return false;
    if (isAnswered(q.type, answers.get(q.id)?.value)) return false;
    // A required WEIGHTED_MULTIPLE_CHOICE with zero options can never be
    // answered — don't let it deadlock submit.
    return !(q.type === "WEIGHTED_MULTIPLE_CHOICE" && optionsEmpty(q.options));
  });
  const totalQuestions = allQuestions.length;
  const canSubmit = answeredCount >= 1 && missingRequired.length === 0;
  const activeDomain = domains[activeDomainIdx];

  function doSubmit() {
    const payload = allQuestions
      .filter((q) => isAnswered(q.type, answers.get(q.id)?.value))
      .map((q) => ({ questionId: q.id, response: answers.get(q.id)!.value }));
    submitMutation.mutate(payload);
  }

  // ── Render: start ──────────────────────────────────────────────────────
  if (step === "start") {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-[440px] rounded-card border border-border-default bg-card p-8">
          <div className="mb-6 flex flex-col items-center gap-2 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-bg">
              <ShieldCheck className="h-6 w-6 text-accent" />
            </div>
            <h1 className="text-[18px] font-semibold text-text-primary">
              {t("external.accessTitle")}
            </h1>
            <p className="text-body text-text-muted">
              {t("external.accessDesc")}
            </p>
          </div>
          <Button
            className="w-full"
            onClick={() => requestPinMutation.mutate()}
            isLoading={requestPinMutation.isPending}
          >
            {t("external.sendPin")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // ── Render: pin ────────────────────────────────────────────────────────
  if (step === "pin") {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-[400px] rounded-card border border-border-default bg-card p-8">
          <div className="mb-6 flex flex-col items-center gap-2 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-bg">
              <Hash className="h-6 w-6 text-accent" />
            </div>
            <h1 className="text-[18px] font-semibold text-text-primary">{t("vendorPortal.enterPin")}</h1>
            <p className="text-body text-text-muted">
              {t("external.pinDesc")}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (pin.length === 6) verifyPinMutation.mutate();
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col items-center gap-2">
              <PinInput
                value={pin}
                onChange={(v) => {
                  setPin(v);
                  setPinError(null);
                }}
              />
              {pinError && <p className="text-[13px] text-danger">{pinError}</p>}
            </div>
            <Button
              type="submit"
              className="w-full"
              isLoading={verifyPinMutation.isPending}
              disabled={pin.length !== 6}
            >
              {t("external.verifyPin")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <button
              type="button"
              onClick={() => requestPinMutation.mutate()}
              disabled={requestPinMutation.isPending}
              className="text-center text-caption text-text-muted hover:text-text-primary"
            >
              {t("external.resendPin")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Render: done ───────────────────────────────────────────────────────
  if (step === "done") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <CheckCircle2 className="h-16 w-16 text-success" />
        <p className="text-[20px] font-semibold text-text-primary">{t("external.thankYou")}</p>
        <p className="max-w-md text-body text-text-muted">
          {t("external.thankYouDesc")}
        </p>
      </div>
    );
  }

  // ── Render: fill ───────────────────────────────────────────────────────
  if (qLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <PageSkeleton />
      </div>
    );
  }

  if (qError && (qError as { status?: number }).status !== 401) {
    // A 401 is handled by the effect above (bounces back to "start"); anything
    // else (404/revoked/expired share) is terminal for this link.
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-[16px] font-semibold text-text-primary">{t("external.linkUnavailable")}</p>
        <p className="max-w-md text-body text-text-muted">
          {(qError as Error).message || t("external.linkUnavailableDesc")}
        </p>
      </div>
    );
  }

  if (!assessment) {
    // Still loading, or the 401-recovery effect just fired this render.
    return (
      <div className="flex flex-1 items-center justify-center">
        <PageSkeleton />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Sub-header with entity/schema name + progress */}
      <div className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-border-default bg-card px-8 py-3">
        <div>
          <p className="text-[14px] font-medium text-text-primary">
            {assessment.entity?.name ?? t("assessment.fallbackName")}
          </p>
          <p className="text-caption text-text-muted">{assessment.schema.name}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-40 overflow-hidden rounded-full bg-hover">
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{
                  width: `${totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0}%`,
                }}
              />
            </div>
            <span className="text-caption text-text-muted">
              {answeredCount}/{totalQuestions}
            </span>
          </div>
          {!canSubmit && missingRequired.length > 0 && (
            <span className="text-[12px] text-text-muted">
              {missingRequired.length} {t("assessment.requiredUnansweredSuffix")}
            </span>
          )}
          <Button
            size="sm"
            onClick={() => setSubmitOpen(true)}
            isLoading={submitMutation.isPending}
            disabled={!canSubmit}
          >
            <Send className="mr-1.5 h-3.5 w-3.5" />
            {t("common.submit")}
          </Button>
        </div>
      </div>

      {/* Body: section nav + content */}
      <div className="flex flex-1 overflow-hidden">
        <div className="w-[300px] flex-shrink-0 overflow-y-auto border-r border-border-default bg-card p-4">
          <p className="mb-2 text-caption font-medium uppercase tracking-wider text-text-muted">
            {t("vendorPortal.sections")}
          </p>
          <nav className="flex flex-col gap-0.5">
            {domains.map((domain, idx) => {
              const isActive = idx === activeDomainIdx;
              const domainTotal = domain.questions.length;
              const domainAnswered = domain.questions.filter((q) =>
                isAnswered(q.type, answers.get(q.id)?.value)
              ).length;
              return (
                <button
                  key={domain.id}
                  type="button"
                  onClick={() => setActiveDomainIdx(idx)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-control px-3 py-2 text-left text-body transition-colors",
                    isActive
                      ? "bg-accent-bg text-accent-text"
                      : "text-text-secondary hover:bg-hover hover:text-text-primary"
                  )}
                >
                  <span>{domain.name}</span>
                  <span className={cn("text-caption", isActive ? "text-accent-text" : "text-text-muted")}>
                    {domainAnswered}/{domainTotal}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          {activeDomain ? (
            <div className="flex flex-col gap-6">
              <h2 className="text-[20px] font-semibold text-text-primary">{activeDomain.name}</h2>

              <div className="flex flex-col gap-3">
                {[...activeDomain.questions]
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((question, qIdx) => {
                    const ans = answers.get(question.id) ?? { value: "", comment: "" };
                    return (
                      <QuestionCard
                        key={question.id}
                        questionId={question.id}
                        index={qIdx + 1}
                        code={question.code}
                        text={question.text}
                        helpText={question.helpText ?? undefined}
                        type={question.type}
                        options={question.options}
                        value={ans.value}
                        comment={ans.comment || null}
                        onValueChange={handleValueChange}
                        onCommentChange={handleCommentChange}
                      />
                    );
                  })}
              </div>

              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setActiveDomainIdx((i) => Math.max(0, i - 1))}
                  disabled={activeDomainIdx === 0}
                >
                  <ChevronLeft className="mr-1.5 h-4 w-4" />
                  {t("vendorPortal.previousSection")}
                </Button>
                <span className="text-caption text-text-muted">
                  {activeDomainIdx + 1} {t("common.of")} {domains.length}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setActiveDomainIdx((i) => Math.min(domains.length - 1, i + 1))}
                  disabled={activeDomainIdx === domains.length - 1}
                >
                  {t("vendorPortal.nextSection")}
                  <ChevronRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-24 text-text-muted">
              {t("external.noSections")}
            </div>
          )}
        </div>
      </div>

      {/* Submit is irreversible and ends the session — confirm first. */}
      <ConfirmDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        isDestructive={false}
        title={t("external.submitConfirmTitle")}
        description={t("external.submitConfirmDesc")}
        confirmLabel={t("common.submit")}
        isLoading={submitMutation.isPending}
        onConfirm={() => {
          setSubmitOpen(false);
          doSubmit();
        }}
      />
    </div>
  );
}
